"use client";

import Image from "next/image";
import Link from "next/link";
import { Button } from "@/components";
import {
  DANK_OPEN_DELIVERY_POSTAL_MODAL,
  DANK_OPEN_PICKUP_STORE_MODAL,
} from "@/lib/header-events";

const CARDS = [
  {
    title: "Same-day delivery",
    image: "/media/pages/how-delivery-works/delivery-menu/same-day-delivery.webp",
    imageAlt: "Same-day delivery",
    cta: "Order delivery",
    action: "postal" as const,
  },
  {
    title: "Mail-order delivery",
    image: "/media/pages/how-delivery-works/delivery-menu/mail-order-delivery.webp",
    imageAlt: "Mail-order delivery",
    cta: "Shop Mail Order",
    action: "mail" as const,
    href: "/search?mailOrder=true",
  },
  {
    title: "Pick-up at store",
    image: "/media/pages/how-delivery-works/delivery-menu/pickup-at-store.webp",
    imageAlt: "Pick-up at store",
    cta: "Shop for pickup",
    action: "pickup" as const,
  },
] as const;

function openDeliveryPostalModal() {
  window.dispatchEvent(new CustomEvent(DANK_OPEN_DELIVERY_POSTAL_MODAL));
}

function openPickupStoreModal() {
  window.dispatchEvent(new CustomEvent(DANK_OPEN_PICKUP_STORE_MODAL));
}

type SelectDeliveryMenuSectionProps = {
  compact?: boolean;
};

export default function SelectDeliveryMenuSection({ compact = false }: SelectDeliveryMenuSectionProps) {
  return (
    <div
      role="region"
      className="w-full"
      aria-labelledby="select-delivery-menu-heading"
    >
      <div className="w-full">
        <h2
          id="select-delivery-menu-heading"
          className="text-h3-mobile sm:text-h3-desktop font-bold text-text-primary leading-tight"
        >
          Select delivery menu
        </h2>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-3">
          {CARDS.map((card) => (
            <div
              key={card.title}
              className="relative aspect-[444/230] w-full overflow-hidden rounded-2xl border-2 border-neutral-10 bg-blue-5"
            >
              <Image
                src={card.image}
                alt={card.imageAlt}
                fill
                className="object-cover object-center"
                sizes="(max-width: 768px) 100vw, 33vw"
                quality={100}
                unoptimized
              />

              <div className={`absolute inset-0 z-10 flex flex-col justify-between ${compact ? "p-4" : "p-5"}`}>
                <h3 className={`font-bold leading-tight text-blue-70 drop-shadow-sm ${compact ? "text-h4-desktop" : "text-body-18 xl:text-h4-desktop"}`}>
                  {card.title}
                </h3>

                {card.action === "mail" ? (
                  <Button asChild variant="bordered" color="white" size="lg" className="w-fit rounded-xl">
                    <Link href={card.href}>
                      {card.cta}
                    </Link>
                  </Button>
                ) : (
                  <Button
                    variant="bordered"
                    color="white"
                    size="lg"
                    onClick={
                      card.action === "postal"
                        ? openDeliveryPostalModal
                        : openPickupStoreModal
                    }
                    className="w-fit rounded-xl px-4 py-3"
                  >
                    {card.cta}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
